/**
 * AI 弹窗内嵌文件管理器：目录列举 + 点击文件在右侧 CodeMirror 编辑器中打开
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFileEditorStore } from '@/stores';

type WsSocket = {
  emit: (event: string, data?: unknown) => void;
  on: (event: string, callback: (data: unknown) => void) => void;
  off: (event: string, callback?: (data: unknown) => void) => void;
};

type WsClientLike = {
  socket?: WsSocket;
  is_connected?: () => boolean;
};

export type FileEntry = {
  name: string;
  isDir: boolean;
  isLink?: boolean;
  size: number;
  mtime: number;
};

function joinPosix(dir: string, name: string): string {
  const left = dir.endsWith('/') ? dir.slice(0, -1) : dir;
  return `${left}/${name}`;
}

function stripTrailingSlash(s: string): string {
  const t = s.replace(/\/+$/, '');
  return t === '' ? '/' : t;
}

function parentDir(path: string, root: string): string | null {
  const p = stripTrailingSlash(path);
  const r = stripTrailingSlash(root);
  if (p === r) return null;
  const i = p.lastIndexOf('/');
  if (i <= 0) return r === '/' ? null : r;
  const parent = p.slice(0, i) || '/';
  if (r !== '/' && !(parent === r || parent.startsWith(`${r}/`))) return null;
  return parent;
}

function formatSize(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function formatTime(ts: number): string {
  if (!ts) return '—';
  try {
    return new Date(ts * 1000).toLocaleString();
  } catch {
    return '—';
  }
}

function buildBreadcrumb(root: string, currentPath: string): { label: string; path: string }[] {
  const r = stripTrailingSlash(root);
  const p = stripTrailingSlash(currentPath);
  const out: { label: string; path: string }[] = [];
  if (r === '/') {
    out.push({ label: '/', path: '/' });
    if (p === '/') return out;
    const parts = p.split('/').filter(Boolean);
    let acc = '';
    for (const seg of parts) {
      acc = acc ? `${acc}/${seg}` : `/${seg}`;
      out.push({ label: seg, path: acc });
    }
    return out;
  }
  out.push({ label: r.split('/').pop() || r, path: r });
  if (p === r) return out;
  if (!p.startsWith(`${r}/`) && p !== r) {
    return [{ label: p.split('/').pop() || p, path: p }];
  }
  const suffix = p.slice(r.length).replace(/^\//, '');
  let acc = r;
  for (const seg of suffix.split('/').filter(Boolean)) {
    acc = `${acc}/${seg}`;
    out.push({ label: seg, path: acc });
  }
  return out;
}

interface FileManagerPanelProps {
  wsClient: WsClientLike | null;
  serverId: number;
  connected: boolean;
  initialPath?: string;
}

export const FileManagerPanel: React.FC<FileManagerPanelProps> = ({
  wsClient,
  serverId,
  connected,
  initialPath = '/',
}) => {
  const [root, setRoot] = useState('/');
  const [currentPath, setCurrentPath] = useState(initialPath);
  const [entries, setEntries] = useState<FileEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploadPreparing, setUploadPreparing] = useState(false);
  const [uploadHint, setUploadHint] = useState<string | null>(null);
  /** 与 currentPath 同步，可手动编辑后跳转 */
  const [pathInput, setPathInput] = useState(initialPath);

  const listReqRef = useRef(0);
  const readReqRef = useRef(0);
  const uploadReqRef = useRef(0);
  const pendingListIdRef = useRef<string | null>(null);
  const pendingReadIdRef = useRef<string | null>(null);
  const pendingUploadIdRef = useRef<string | null>(null);

  const breadcrumb = useMemo(() => buildBreadcrumb(root, currentPath), [root, currentPath]);

  const requestList = useCallback(
    (path: string) => {
      const sock = wsClient?.socket;
      if (!sock || !connected) {
        setError('未连接，无法列举目录');
        return;
      }
      const request_id = `list_${++listReqRef.current}_${Date.now()}`;
      pendingListIdRef.current = request_id;
      setLoading(true);
      setError(null);
      sock.emit('file_manager_list', { server_id: serverId, path, request_id });
    },
    [wsClient, connected, serverId]
  );

  useEffect(() => {
    setCurrentPath(initialPath);
  }, [initialPath, serverId]);

  useEffect(() => {
    setPathInput(currentPath);
  }, [currentPath]);

  const normalizePathInput = useCallback((raw: string) => {
    let p = raw.trim();
    if (!p) return '/';
    if (!p.startsWith('/')) p = `/${p}`;
    return stripTrailingSlash(p) || '/';
  }, []);

  const goToPathInput = useCallback(() => {
    const p = normalizePathInput(pathInput);
    setPathInput(p);
    if (p === stripTrailingSlash(currentPath) || p === currentPath) {
      requestList(p);
      return;
    }
    setCurrentPath(p);
  }, [pathInput, currentPath, normalizePathInput, requestList]);

  useEffect(() => {
    const sock = wsClient?.socket;
    if (!sock) return;

    const onListResult = (data: unknown) => {
      const d = data as {
        request_id?: string;
        path?: string;
        root?: string;
        entries?: FileEntry[];
      };
      if (!d || typeof d.path !== 'string') return;
      if (d.request_id && d.request_id !== pendingListIdRef.current) return;
      setLoading(false);
      setError(null);
      if (typeof d.root === 'string' && d.root) setRoot(d.root);
      setCurrentPath(d.path);
      setEntries(Array.isArray(d.entries) ? d.entries : []);
    };

    const onReadResult = (data: unknown) => {
      const d = data as {
        request_id?: string;
        path?: string;
        binary?: boolean;
        content?: string;
        message?: string;
        truncated?: boolean;
        size?: number;
        error?: string;
      };
      if (!d || typeof d.path !== 'string') return;
      if (d.request_id && d.request_id !== pendingReadIdRef.current) return;
      setLoading(false);
      setError(null);
      if (d.binary) {
        setError('无法预览该文件（二进制格式）');
        return;
      }
      if (d.error) {
        setError(d.error);
        return;
      }
      if (d.truncated) {
        setError('内容已截断至服务器允许的最大长度');
      }
      const text = typeof d.content === 'string' ? d.content : '';
      // 在右侧 CodeMirror 编辑器中打开
      useFileEditorStore.getState().openFileInEditor({
        path: d.path,
        content: text,
        size: d.size || text.length,
      });
    };

    const onError = (data: unknown) => {
      const d = data as { op?: string; message?: string; request_id?: string };
      if (d?.op !== 'list' && d?.op !== 'read') return;
      if (d.request_id) {
        if (d.op === 'list' && d.request_id !== pendingListIdRef.current) return;
        if (d.op === 'read' && d.request_id !== pendingReadIdRef.current) return;
      }
      setLoading(false);
      setError(typeof d.message === 'string' ? d.message : '操作失败');
    };

    const onUploadReady = (data: unknown) => {
      const d = data as {
        request_id?: string;
        open_url?: string;
      };
      if (d?.request_id && d.request_id !== pendingUploadIdRef.current) return;
      setUploadPreparing(false);
      pendingUploadIdRef.current = null;
      setError(null);
      const openUrl = typeof d.open_url === 'string' ? d.open_url : '';
      if (!openUrl) {
        setError('上传页地址无效');
        return;
      }
      setUploadHint(null);
      const w = window.open(openUrl, '_blank', 'noopener,noreferrer');
      if (!w) setError('弹窗被拦截，请允许本站弹出窗口后重试。');
    };

    const onUploadErr = (data: unknown) => {
      const d = data as { request_id?: string; code?: string; message?: string };
      if (d?.request_id && d.request_id !== pendingUploadIdRef.current) return;
      setUploadPreparing(false);
      pendingUploadIdRef.current = null;
      setError(typeof d.message === 'string' ? d.message : '准备上传失败');
    };

    sock.on('file_manager_list_result', onListResult);
    sock.on('file_manager_read_result', onReadResult);
    sock.on('file_manager_error', onError);
    sock.on('upload_agent_ready', onUploadReady);
    sock.on('upload_agent_error', onUploadErr);

    return () => {
      sock.off('file_manager_list_result', onListResult);
      sock.off('file_manager_read_result', onReadResult);
      sock.off('file_manager_error', onError);
      sock.off('upload_agent_ready', onUploadReady);
      sock.off('upload_agent_error', onUploadErr);
    };
  }, [wsClient?.socket]);

  useEffect(() => {
    const onMsg = (ev: MessageEvent) => {
      const d = ev.data;
      if (!d || typeof d !== 'object') return;
      if ((d as { type?: string }).type !== 'sse_file_upload_done') return;
      setUploadHint(null);
      requestList(currentPath);
    };
    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
  }, [currentPath, requestList]);

  useEffect(() => {
    if (!connected || !wsClient?.socket) return;
    requestList(currentPath);
  }, [connected, serverId, currentPath, wsClient?.socket, requestList]);

  const handleOpenEntry = (e: FileEntry) => {
    if (e.isDir) {
      setCurrentPath(joinPosix(currentPath, e.name));
      return;
    }
    const sock = wsClient?.socket;
    if (!sock || !connected) {
      setError('未连接，无法读取文件');
      return;
    }
    const fullPath = joinPosix(currentPath, e.name);
    const request_id = `read_${++readReqRef.current}_${Date.now()}`;
    pendingReadIdRef.current = request_id;
    setLoading(true);
    setError(null);
    sock.emit('file_manager_read', { server_id: serverId, path: fullPath, request_id });
  };

  const up = parentDir(currentPath, root);
  const showUp = up != null;

  const uploadButtonTitle = !connected
    ? '未连接，无法上传'
    : loading
      ? '目录加载中，请稍候再试'
      : uploadPreparing
        ? '正在安装服务…'
        : '上传文件';

  const handlePrepareUpload = useCallback(() => {
    const sock = wsClient?.socket;
    if (!sock || !connected) {
      setError('未连接，无法准备上传');
      return;
    }
    const request_id = `upload_${++uploadReqRef.current}_${Date.now()}`;
    pendingUploadIdRef.current = request_id;
    setUploadPreparing(true);
    setUploadHint(null);
    setError(null);
    sock.emit('upload_agent_prepare', {
      server_id: serverId,
      path: stripTrailingSlash(currentPath) || '/',
      request_id,
    });
  }, [wsClient, connected, serverId, currentPath]);

  return (
    <div className="sse-ai-file-panel" role="region" aria-label="远程文件">
      <div className="sse-ai-file-panel-toolbar">
        <button
          type="button"
          className="sse-ai-file-refresh"
          disabled={!connected || loading}
          onClick={() => requestList(currentPath)}
          title="刷新"
        >
          <span className="dashicons dashicons-update" aria-hidden />
        </button>
        <button
          type="button"
          className={`sse-ai-file-upload${uploadPreparing ? ' is-preparing' : ''}`}
          disabled={!connected || loading || uploadPreparing}
          onClick={handlePrepareUpload}
          title={uploadButtonTitle}
          aria-busy={uploadPreparing}
          aria-label={uploadButtonTitle}
        >
          <span
            className={`dashicons ${uploadPreparing ? 'dashicons-update' : 'dashicons-upload'}`}
            aria-hidden
          />
        </button>
        {showUp && (
          <button
            type="button"
            className="sse-ai-file-up"
            onClick={() => {
              if (up == null) return;
              setCurrentPath(up);
            }}
            title="上级目录"
          >
            ..
          </button>
        )}
      </div>

      <div className="sse-ai-file-path-row">
        <input
          type="text"
          className="sse-ai-file-path-input"
          value={pathInput}
          onChange={(e) => setPathInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              goToPathInput();
            }
          }}
          placeholder="绝对路径，回车跳转"
          aria-label="目录路径"
          spellCheck={false}
          autoComplete="off"
          disabled={!connected}
        />
        <button
          type="button"
          className="sse-ai-file-path-go"
          disabled={!connected || loading}
          onClick={goToPathInput}
          title="跳转到该路径"
        >
          前往
        </button>
      </div>

      <nav className="sse-ai-file-breadcrumb" aria-label="路径面包屑">
        {breadcrumb.map((seg, i) => (
          <React.Fragment key={`${seg.path}_${i}`}>
            {i > 0 && breadcrumb[i - 1].path !== '/' && (
              <span className="sse-ai-file-bc-sep">/</span>
            )}
            <button
              type="button"
              className="sse-ai-file-bc-item"
              onClick={() => {
                setCurrentPath(seg.path);
              }}
            >
              {seg.label}
            </button>
          </React.Fragment>
        ))}
      </nav>

      {uploadPreparing && (
        <div className="sse-ai-file-note" role="status" aria-live="polite">
          正在安装服务…
        </div>
      )}
      {uploadHint && !error && <div className="sse-ai-file-upload-hint">{uploadHint}</div>}
      {error && <div className="sse-ai-file-error">{error}</div>}

      <div className="sse-ai-file-list-wrap">
        {loading && <div className="sse-ai-file-loading">加载中…</div>}
        <ul className="sse-ai-file-list">
          {entries.map((e) => (
            <li key={e.name}>
              <button
                type="button"
                className={`sse-ai-file-row ${e.isDir ? 'is-dir' : 'is-file'}`}
                onClick={() => handleOpenEntry(e)}
              >
                <span className="sse-ai-file-name">
                  {e.isDir ? '📁 ' : e.isLink ? '🔗 ' : '📄 '}
                  {e.name}
                </span>
                <span className="sse-ai-file-meta">
                  {!e.isDir && formatSize(e.size)}
                  {e.isDir && ' '}
                  {!e.isDir && ' · '}
                  {formatTime(e.mtime)}
                </span>
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};
